import { useCallback, useEffect, useRef, useState } from "react";

import { getYouTubeVideoId } from "../../../../../utils/getYouTubeVideoId";

import Button from "../../ButtonComponent";
import HeaderModule from "../../ModuleHeaderComponent";

import { BUTTON_VARIANTS } from "../../constant";

import styles from "./styles.module.scss";
import Shimmer from "../../ShimmerComponent";
import { useGetTranslatedText } from "../../queries";
import { getTranslatedText } from "../../../../../utils/getTranslatedText";

const getVideoUrl = (videoData = {}) => {
  const possibleUrls = [
    videoData?.videoUrl,
    videoData?.video_url,
    videoData?.vimeoUrl,
    videoData?.vimeo_url,
    videoData?.videoLink,
    videoData?.video_link,
    videoData?.contentUrl,
    videoData?.content_url,
    videoData?.fileUrl,
    videoData?.file_url,
    videoData?.url,
    videoData?.link,
    videoData?.video,
  ];

  return (
    possibleUrls.find(
      (url) => typeof url === "string" && url.trim()
    )?.trim() || ""
  );
};

const getVimeoVideoId = (url = "") => {
  const videoUrl = String(url).trim();

  if (!videoUrl) return null;

  if (/^\d+$/.test(videoUrl)) return videoUrl;

  const decodedUrl = decodeURIComponent(videoUrl);
  const match = decodedUrl.match(
    /(?:vimeo\.com\/(?:.*\/)?|player\.vimeo\.com\/video\/)(\d+)(?:[/?#]|$)/i
  );

  return match?.[1] || null;
};

const InclusiveEducationVideo = (props) => {
  const {
    inclusiveEducationContent,
    isLoading,
    handleClick,
    handleBackClick,
    isPending,
    isFetching,
  } = props;

  const [isVideoCompleted, setIsVideoCompleted] = useState(false);

  const iframeRef = useRef(null);

  const languageId = localStorage.getItem("l_id") || 1;

  const { data: translatedTextResponse } =
    useGetTranslatedText(languageId);

  const { data: translatedText = [] } =
    translatedTextResponse || {};

  const { data: inclusiveEducationContentData } =
    inclusiveEducationContent || {};

  const { response } = inclusiveEducationContentData || {};

  const videoData = response?.videos?.[0];

  const videoStatus = videoData?.videoStatus;

  const videoUrl = getVideoUrl(videoData);
  const normalizedVideoUrl = videoUrl.toLowerCase();

  const isYoutube =
    normalizedVideoUrl.includes("youtube.com") ||
    normalizedVideoUrl.includes("youtu.be");

  const isVimeo = 
    normalizedVideoUrl.includes("vimeo.com") ||
    normalizedVideoUrl.includes("player.vimeo.com");

  const youtubeVideoId = isYoutube
    ? getYouTubeVideoId(
        videoUrl.replace("watch/", "watch?v=")
      )
    : null;

  const vimeoVideoId = isVimeo
    ? getVimeoVideoId(videoUrl)
    : null;

  const youtubePlayerId = youtubeVideoId
    ? `inclusive-education-youtube-${youtubeVideoId}`
    : "";

  const vimeoPlayerId = vimeoVideoId
    ? `inclusive-education-vimeo-${vimeoVideoId}`
    : "";

  const iframeTitle = videoData?.title || "Inclusive education video";

  const youtubeEmbedUrl = youtubeVideoId
    ? `https://www.youtube.com/embed/${youtubeVideoId}?enablejsapi=1&origin=${window.location.origin}&controls=0&modestbranding=1&rel=0&fs=0&iv_load_policy=3&disablekb=1`
    : "";

  const vimeoEmbedUrl = vimeoVideoId
    ? `https://player.vimeo.com/video/${vimeoVideoId}?api=1&player_id=${vimeoPlayerId}&title=0&byline=0&portrait=0`
    : "";

  const iframeUrl = youtubeEmbedUrl || vimeoEmbedUrl || videoUrl;
  const iframePlayerId = youtubePlayerId || vimeoPlayerId;

  const registerIframeEvents = useCallback(() => {
    if (!iframeRef.current?.contentWindow) return;

    if (isYoutube) {
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({
          event: "command",
          func: "addEventListener",
          args: ["onStateChange"],
          id: youtubePlayerId,
        }),
        "https://www.youtube.com"
      );

      return;
    }

    if (!isVimeo) return;

    iframeRef.current.contentWindow.postMessage(
      JSON.stringify({
        method: "addEventListener",
        value: "ended",
      }),
      "https://player.vimeo.com"
    );
  }, [isVimeo, isYoutube, youtubePlayerId]);

  useEffect(() => {
    setIsVideoCompleted(false);
  }, [videoUrl]);

  useEffect(() => {
    if (!isYoutube && !isVimeo) return;

    const handleIframeMessage = (event) => {
      if (
        !event.origin.includes("youtube.com") &&
        !event.origin.includes("vimeo.com")
      ) {
        return;
      }

      let data = event.data;

      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (error) {
          return;
        }
      }

      if (
        data?.id === youtubePlayerId &&
        data?.event === "onStateChange" &&
        data?.info === 0
      ) {
        setIsVideoCompleted(true);
      }

      if (
        data?.event === "infoDelivery" &&
        data?.info?.playerState === 0
      ) {
        setIsVideoCompleted(true);
      }

      if (
        data?.player_id === vimeoPlayerId &&
        data?.event === "ready"
      ) {
        registerIframeEvents();
      }

      if (
        data?.player_id === vimeoPlayerId &&
        (data?.event === "ended" || data?.event === "finish")
      ) {
        setIsVideoCompleted(true);
      }
    };

    window.addEventListener("message", handleIframeMessage);

    return () =>
      window.removeEventListener(
        "message",
        handleIframeMessage
      );
  }, [
    isVimeo,
    isYoutube,
    registerIframeEvents,
    vimeoPlayerId,
    youtubePlayerId,
  ]);

  return (
    <>
      <HeaderModule
        title={videoData?.title}
        className={styles["header-title"]}
        onBackClick={handleBackClick}
      />

      {isLoading || isFetching ? (
        <Shimmer
          width="65%"
          height="75vh"
          borderRadius="14px"
          style={{ margin: "20px auto" }}
        />
      ) : (
        <div className={styles["video-container-wrapper"]}>
          <div className={styles["video-player"]}>
            {iframeUrl && (
              <iframe
                ref={iframeRef}
                id={iframePlayerId}
                title={iframeTitle}
                src={iframeUrl}
                className={styles["video-container"]}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                onLoad={registerIframeEvents}
              />
            )}
          </div>
        </div>
      )}

      <div className={styles["bottom-wrapper"]}>
        <Button
          label={getTranslatedText(
            translatedText,
            "label_Continue",
            "Continue"
          )}
          buttonVariant={BUTTON_VARIANTS.PRIMARY}
          disabled={
            !(videoStatus === 1 || isVideoCompleted) ||
            isPending
          }
          className={styles["button-class"]}
          onClick={handleClick}
        />
      </div>
    </>
  );
};

export default InclusiveEducationVideo;
